import { computed, makeObservable } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { Opponent } from '@gem/call/CallStore/Opponent/Opponent';

const SILENCE_BEFORE_DROP = 3_000;

interface IOpponentMeta {
  uid: ProviderUid;
  hasVideo: boolean;
  hasAudio: boolean;
  isSharedScreen: boolean;
  isWhoSharedScreen: boolean;
  isActiveSpeaker: boolean;
  isMe: boolean;
  timeStamp: number;
  sortIndex: number;
}

const getOpponentMeta = (item: Opponent): Omit<IOpponentMeta, 'timeStamp' | 'sortIndex'> => {
  return {
    uid: item.uid,
    hasVideo: item.hasVideo,
    hasAudio: item.hasAudio,
    isSharedScreen: item.isSharedScreen,
    isWhoSharedScreen: !item.isSharedScreen && !!item.memberDevice?.hasScreenSharingOpponent,
    isActiveSpeaker: item.isActiveSpeaker,
    isMe: item.memberDevice?.isMyDevice ?? false,
  };
};

const getSortValue = (opponent: Partial<IOpponentMeta>, isGridLayout: boolean): number => {
  const { isSharedScreen, isMe, isWhoSharedScreen, hasVideo, hasAudio } = opponent;

  if (isGridLayout) {
    if (isSharedScreen) return 7;
    if (isMe) return 6;
  }

  if (hasAudio && hasVideo && isWhoSharedScreen) return 5;
  if (hasAudio && hasVideo && !isWhoSharedScreen) return 4;
  if (hasAudio && !hasVideo && isWhoSharedScreen) return 3;
  if (hasAudio && !hasVideo && !isWhoSharedScreen) return 2;
  if (!hasAudio && hasVideo) return 1;

  return 0;
};

const callOpponentsSort = (opponent1: IOpponentMeta, opponent2: IOpponentMeta): number => {
  if (opponent1.sortIndex === opponent2.sortIndex) {
    return opponent2.timeStamp - opponent1.timeStamp;
  }
  return opponent2.sortIndex - opponent1.sortIndex;
};
export class OpponentsPositions {
  prevOpponentsMeta: Record<string, IOpponentMeta> = {};
  audioEnabledTime: Record<ProviderUid, number> = {};
  silenceStartTime: Record<ProviderUid, number> = {};
  call: Call;

  constructor(call: Call) {
    makeObservable(this);
    this.call = call;
  }

  private shouldDropDueToSilence(
    uid: ProviderUid,
    hasAudio: boolean,
    isActiveSpeaker: boolean
  ): boolean {
    if (!hasAudio || isActiveSpeaker) {
      delete this.silenceStartTime[uid];
      return false;
    }

    const now = Date.now();
    const silenceTime = this.silenceStartTime[uid];

    if (!silenceTime) {
      this.silenceStartTime[uid] = now;
      return false;
    }

    return now - silenceTime >= SILENCE_BEFORE_DROP;
  }

  private updateAudioEnabledTime(uid: ProviderUid, hasAudio: boolean, now: number): void {
    if (hasAudio && !this.audioEnabledTime[uid]) {
      this.audioEnabledTime[uid] = now;
    } else if (!hasAudio) {
      delete this.audioEnabledTime[uid];
    }
  }

  private getTimeStamp(
    meta: Omit<IOpponentMeta, 'timeStamp' | 'sortIndex'>,
    sortIndex: number,
    shouldDrop: boolean,
    now: number
  ): number {
    const prevMeta = this.prevOpponentsMeta[meta.uid];
    const hasAnyActiveSpeakers = this.call.opponents.opponentList.some(
      (opponent) => opponent.isActiveSpeaker
    );

    if (!hasAnyActiveSpeakers && prevMeta) {
      return prevMeta.timeStamp;
    }

    if (meta.hasAudio && !shouldDrop) {
      return this.audioEnabledTime[meta.uid] || prevMeta?.timeStamp || now;
    }

    if (prevMeta && prevMeta.sortIndex === sortIndex) {
      return prevMeta.timeStamp;
    }

    return now;
  }

  @computed
  get opponentsMeta(): Record<ProviderUid, IOpponentMeta> {
    const isGridLayout = this.call.viewState.isGridLayout;
    const now = Date.now();

    const opponents = this.call.opponents.opponentList.reduce<Record<ProviderUid, IOpponentMeta>>(
      (acc, item) => {
        const meta = getOpponentMeta(item);

        this.updateAudioEnabledTime(item.uid, meta.hasAudio, now);

        const shouldDrop = this.shouldDropDueToSilence(
          item.uid,
          meta.hasAudio,
          meta.isActiveSpeaker
        );

        let sortIndex = getSortValue(meta, isGridLayout);

        if (shouldDrop && meta.hasAudio) {
          sortIndex -= 0.5;
        }
        const timeStamp = this.getTimeStamp(meta, sortIndex, shouldDrop, now);

        acc[item.uid] = {
          ...meta,
          sortIndex,
          timeStamp,
        };

        return acc;
      },
      {}
    );

    this.prevOpponentsMeta = opponents;

    return opponents;
  }

  @computed
  get opponentsPositions(): Record<string, number> {
    return Object.values(this.opponentsMeta)
      .sort(callOpponentsSort)
      .reduce<Record<string, number>>((acc, item, index) => {
        acc[item.uid] = index;

        return acc;
      }, {});
  }
}
